import { observer } from 'mobx-react';
import React from 'react';
import { NavItem } from 'reactstrap';
import CopyableText from '~/components/CopyableText';
import CopyToClipboardButton from '~/components/CopyToClipboardButton';
import HelpPopover from '~/components/HelpPopover';
import { LoadStatus } from '~/lib/store/LoadingTracker';
import { SupportCodeStore, supportCodeClipboardText, supportCodeExplanation } from '~/lib/store/SupportCodeStore';
import './SupportCodeNav.scss';

const placeholderCode = '0000-0000-0000-0000';

// Skids an end-aligned popover past its rounded corner, so the arrow meets the straight edge.
const endAlignedOffset: [number, number] = [8, 8];

type SupportCodeNavProps = {
    store: SupportCodeStore;
};

// End-aligned so the popover grows upward from the icon, clear of the code in the row below it. On a phone
// there is no room beside the sidebar, so it opens above the icon instead.
function explainer() {
    return (
        <HelpPopover
            label="What is the support code?"
            header="Support code"
            placement="right-end"
            fallbackPlacements={['top-end']}
            offset={endAlignedOffset}
        >
            {supportCodeExplanation}
        </HelpPopover>
    );
}

// Sits immediately above the Help Desk link, where the support journey starts.
@observer
export default class SupportCodeNav extends React.Component<SupportCodeNavProps> {
    componentDidMount() {
        // Caught: a 404 (no code for this account or environment) is a normal state the store's status carries.
        this.props.store.ensureLoaded().catch(() => undefined);
    }

    render() {
        const store = this.props.store;

        // An invisible copy of the block holds its place while the code loads: the profile menu is
        // pinned to the bottom of the sidebar, so a block arriving late would shift the items around
        // it. That includes the reload after a password change, when the code on screen has already
        // stopped verifying.
        if (store.status === LoadStatus.Pending || store.status === LoadStatus.Loading) {
            return this.renderBlock(placeholderCode, true);
        }

        // No code is a normal state, not an error the customer can act on, so render nothing.
        return store.status === LoadStatus.Complete && store.item ? this.renderBlock(store.item, false) : null;
    }

    renderBlock(code: string, placeholder: boolean) {
        // The sidebar closes the mobile drawer on any click inside it, which would hide the
        // explainer or the copy confirmation the instant the tap produced it.
        return (
            <NavItem
                className={placeholder ? 'supportCodeNav supportCodeNavPlaceholder' : 'supportCodeNav'}
                aria-hidden={placeholder || undefined}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="supportCodeNavExpanded">
                    <div className="supportCodeNavLabel">
                        Support code
                        {explainer()}
                    </div>
                    <CopyableText text={supportCodeClipboardText(code)} contextText="support code">
                        <span className="supportCodeNavValue">{code}</span>
                    </CopyableText>
                </div>
                <div className="supportCodeNavCollapsed">
                    <HelpPopover
                        label="Show support code"
                        header="Support code"
                        icon="ion-md-copy"
                        placement="right-end"
                        fallbackPlacements={['top-end']}
                        offset={endAlignedOffset}
                        popperClassName="supportCodePopover"
                    >
                        <div className="supportCodePopoverCode">
                            <span className="supportCodePopoverValue">{code}</span>
                            <CopyToClipboardButton
                                text={supportCodeClipboardText(code)}
                                title="Copy support code"
                                tooltipText="Support code copied"
                                color="link"
                                size="sm"
                            />
                        </div>
                        {supportCodeExplanation}
                    </HelpPopover>
                </div>
            </NavItem>
        );
    }
}
